const USER_PROFILE_STORAGE_KEY = "userProfile";
const POST_SETUP_OVERRIDE_STORAGE_KEY = "postSetupRouteOverride";

const safeSessionParseJSON = (key) => {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (err) {
    console.warn("[authState] Failed to parse sessionStorage key:", key, err);
    return null;
  }
};

export const getPostSetupRouteOverride = (uid) => {
  const stored = safeSessionParseJSON(POST_SETUP_OVERRIDE_STORAGE_KEY);
  if (!stored || !uid || stored.uid !== uid) return null;
  if (typeof stored.expiresAt === "number" && stored.expiresAt < Date.now()) {
    try {
      window.sessionStorage.removeItem(POST_SETUP_OVERRIDE_STORAGE_KEY);
    } catch (err) {
      console.warn("[authState] Failed to clear expired post-setup override:", err);
    }
    return null;
  }
  return stored;
};

export const setPostSetupRouteOverride = ({ uid, route = "/dashboard", planType = "trial", trialEndsAt = null, expiresInMs = 120000 }) => {
  if (!uid) return;
  try {
    window.sessionStorage.setItem(
      POST_SETUP_OVERRIDE_STORAGE_KEY,
      JSON.stringify({
        uid,
        route,
        planType,
        trialEndsAt,
        expiresAt: Date.now() + expiresInMs,
      })
    );
  } catch (err) {
    console.warn("[authState] Failed to cache post-setup route override:", err);
  }
};

export const clearPostSetupRouteOverride = (uid) => {
  try {
    const stored = safeSessionParseJSON(POST_SETUP_OVERRIDE_STORAGE_KEY);
    if (!stored) return;
    if (!uid || stored.uid === uid) {
      window.sessionStorage.removeItem(POST_SETUP_OVERRIDE_STORAGE_KEY);
    }
  } catch (err) {
    console.warn("[authState] Failed to clear post-setup route override:", err);
  }
};

export const getAppBasePath = () => {
  let base = "";
  if (!base) {
    base = (process.env.PUBLIC_URL || "").trim();
  }

  base = (base || "").toString().trim();
  if (base === "/" || base === "") base = "";

  if (base) {
    base = base.replace(/\/+$/g, "");
    if (!base.startsWith("/")) base = `/${base}`;
  }

  if (!base && typeof window !== "undefined") {
    const firstSeg = (window.location.pathname || "").split("/").filter(Boolean)[0];
    if (firstSeg === "app") base = "/app";
  }

  return base;
};

export const getContinueUrlForEmailVerification = () => {
  const base = getAppBasePath();
  return `${window.location.origin}${base}/login?mode=verifyEmail`;
};

export const getEmailVerificationActionCodeSettings = () => ({
  url: getContinueUrlForEmailVerification(),
  handleCodeInApp: true,
});

export const isPasswordProviderUser = (firebaseUser) => {
  const providers = firebaseUser?.providerData || [];
  return providers.some((provider) => provider.providerId === "password");
};

export const mustVerifyEmailAuthOnly = (firebaseUser) => {
  return !!firebaseUser && isPasswordProviderUser(firebaseUser) && !firebaseUser.emailVerified;
};

export const isUserVerified = (firebaseUser) => {
  if (!firebaseUser) return false;
  if (!isPasswordProviderUser(firebaseUser)) return true;
  return firebaseUser.emailVerified === true;
};

export const safeParseJSON = (key) => {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (err) {
    console.warn("[authState] Failed to parse localStorage key:", key, err);
    return null;
  }
};

export const getCachedUserProfile = (uid) => {
  const storedProfileRaw = safeParseJSON(USER_PROFILE_STORAGE_KEY);
  return storedProfileRaw && storedProfileRaw.uid === uid ? storedProfileRaw : null;
};

export const cacheUserProfile = (profile) => {
  if (!profile) return;
  try {
    window.localStorage.setItem(USER_PROFILE_STORAGE_KEY, JSON.stringify(profile));
  } catch (err) {
    console.warn("[authState] Failed to cache userProfile:", err);
  }
};

export const clearCachedUserProfile = () => {
  try {
    window.localStorage.removeItem(USER_PROFILE_STORAGE_KEY);
  } catch (err) {
    console.warn("[authState] Failed to clear cached userProfile:", err);
  }
};

export const toMs = (ts) => {
  if (!ts) return null;
  if (typeof ts?.toMillis === "function") return ts.toMillis();
  if (typeof ts === "object" && typeof ts.seconds === "number") return ts.seconds * 1000;
  if (typeof ts === "string") {
    const parsed = Date.parse(ts);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

export const profileLooksMeaningful = (data) => {
  if (!data) return false;
  return (
    !!data.role ||
    !!data.companyId ||
    !!data.planType ||
    typeof data.companySetupCompleted === "boolean" ||
    typeof data.setupCompleted === "boolean" ||
    typeof data.subscriptionActive === "boolean" ||
    !!data.trialEndsAt
  );
};

export const deriveFlagsFromProfile = (profileInput = {}) => {
  const profile = profileInput || {};

  const rawRole = profile.role || null;
  const normalizedRole = (rawRole || "").toString().trim().toLowerCase();

  const roleKnown = !!normalizedRole;
  const isOwner = normalizedRole === "owner";
  const isCustomer = normalizedRole === "customer";
  const isAdminUser = normalizedRole === "admin" || isOwner;

  const companyId = profile.companyId || null;
  const planType = (profile.planType || "pending").toString().toLowerCase();
  const subscriptionActive = profile.subscriptionActive === true;
  const setupCompleted = profile.setupCompleted === true;
  const rawCompanySetupCompleted = profile.companySetupCompleted === true;

  const trialEndsMs = toMs(profile.trialEndsAt);
  const trialExpired = typeof trialEndsMs === "number" ? trialEndsMs < Date.now() : false;
  const hasActiveTrial = planType === "trial" && !!trialEndsMs && !trialExpired;
  const hasPaidPlan = planType === "paid" && subscriptionActive;

  const companySetupCompleted =
    rawCompanySetupCompleted ||
    (isAdminUser && !!companyId && (setupCompleted || hasActiveTrial || hasPaidPlan));

  const mustProvideCompanyInfo = isAdminUser && !companySetupCompleted;
  const mustUpgradeForExpiredTrial =
    isAdminUser && planType === "trial" && !!trialEndsMs && trialExpired && !hasPaidPlan;
  const mustChoosePlan =
    isAdminUser && companySetupCompleted && !setupCompleted && !hasActiveTrial && !hasPaidPlan;

  let defaultRoute = "/dashboard";
  if (isCustomer) defaultRoute = "/customer";

  return {
    rawRole,
    normalizedRole,
    roleKnown,
    isOwner,
    isCustomer,
    isAdminUser,
    defaultRoute,
    companyId,
    planType,
    subscriptionActive,
    setupCompleted,
    rawCompanySetupCompleted,
    companySetupCompleted,
    trialExpired,
    hasActiveTrial,
    hasPaidPlan,
    mustProvideCompanyInfo,
    mustUpgradeForExpiredTrial,
    mustChoosePlan,
  };
};

export const deriveUserFlags = (firebaseUser, userProfileState) => {
  const uid = firebaseUser?.uid || null;
  const storedProfile = uid ? getCachedUserProfile(uid) : null;
  const postSetupOverride = uid ? getPostSetupRouteOverride(uid) : null;
  let mergedProfile = {
    ...(storedProfile || {}),
    ...(userProfileState || {}),
  };

  if (storedProfile && userProfileState) {
    const storedFlags = deriveFlagsFromProfile(storedProfile);
    const liveFlags = deriveFlagsFromProfile(userProfileState);

    const storedIsFurtherAlong =
      storedFlags.hasActiveTrial ||
      storedFlags.hasPaidPlan ||
      storedFlags.setupCompleted ||
      storedFlags.companySetupCompleted;
    const liveStillNeedsSetup =
      liveFlags.mustProvideCompanyInfo ||
      liveFlags.mustChoosePlan ||
      liveFlags.mustUpgradeForExpiredTrial;

    if (storedIsFurtherAlong && liveStillNeedsSetup) {
      mergedProfile = {
        ...mergedProfile,
        role: storedProfile.role || mergedProfile.role,
        companyId: storedProfile.companyId || mergedProfile.companyId,
        planType: storedProfile.planType || mergedProfile.planType,
        subscriptionActive:
          typeof storedProfile.subscriptionActive === "boolean"
            ? storedProfile.subscriptionActive
            : mergedProfile.subscriptionActive,
        companySetupCompleted:
          typeof storedProfile.companySetupCompleted === "boolean"
            ? storedProfile.companySetupCompleted
            : mergedProfile.companySetupCompleted,
        setupCompleted:
          typeof storedProfile.setupCompleted === "boolean"
            ? storedProfile.setupCompleted
            : mergedProfile.setupCompleted,
        trialEndsAt: storedProfile.trialEndsAt || mergedProfile.trialEndsAt,
      };
    }
  }

  const preOverrideFlags = deriveFlagsFromProfile(mergedProfile);
  if (
    postSetupOverride &&
    (preOverrideFlags.mustProvideCompanyInfo ||
      preOverrideFlags.mustChoosePlan ||
      preOverrideFlags.mustUpgradeForExpiredTrial)
  ) {
    mergedProfile = {
      ...mergedProfile,
      planType: postSetupOverride.planType || mergedProfile.planType || "trial",
      subscriptionActive: true,
      companySetupCompleted: true,
      setupCompleted: true,
      trialEndsAt: postSetupOverride.trialEndsAt || mergedProfile.trialEndsAt,
    };
  }

  const flags = deriveFlagsFromProfile(mergedProfile);
  return {
    uid,
    ...flags,
    postSetupOverride,
    profile: mergedProfile,
  };
};

export const getPostAuthRoute = (flags) => {
  if (!flags) return "/login";
  if (flags.mustProvideCompanyInfo) return "/complete-account-setup";
  if (flags.mustUpgradeForExpiredTrial || flags.mustChoosePlan) {
    return "/complete-account-setup?mode=upgrade";
  }
  return flags.defaultRoute || "/dashboard";
};
